import { z } from 'zod';

export const ModelResultsSchema = z.object({
  statistics: z.array(z.any()), 
  messages: z.array(z.any()),
  errors: z.array(z.any()).optional(),
});

export type ModelResults = z.infer<typeof ModelResultsSchema>;

export const FeatureSchema = z.object({
  featureId: z.string(),
  name: z.string(),
  description: z.string().optional(),
  formula: z.string(),
  results: ModelResultsSchema.optional(),
  created: z.string().optional(),
  lastUpdated: z.string().optional(),
});

export type Feature = z.infer<typeof FeatureSchema>;

export const ModelOperationSchema = z.object({
  type: z.enum(["experiment", "quick_train", "train"]),
  status: z.enum(["starting", "experimenting", "running", "training", "canceling", "canceled", "succeeded", "failed"]),
  progress: z.object({
    currentStage: z.enum(["starting", "tune_models", "backtesting", "generating", "correlation", "ranking_features", "multicollinearity", "ensembles", "quick_train", "training", "canceling", "canceled", "finished", "succeeded", "failed"]).optional(),
    percentComplete: z.number(),
    startedAt: z.union([z.number(), z.string().transform(val => Date.parse(val))]).optional(),
    estimatedCompletion: z.union([z.number(), z.string().transform(val => Date.parse(val))]).optional(),
  }),
});

export type ModelOperation = z.infer<typeof ModelOperationSchema>;

export const CheckpointSchema = z.object({
  id: z.string(),
  versionId: z.string(),
  type: z.enum(['experiment', 'quick_train', 'train']),
  results: ModelResultsSchema.optional(),
  created: z.string(),
  lastUpdated: z.string(),
  features: z.array(FeatureSchema),
  operation: ModelOperationSchema,
});

export type Checkpoint = z.infer<typeof CheckpointSchema>;

export const ModelVersionSchema = z.object({
  id: z.string(),
  status: z.enum(['draft', 'experimenting', 'experimented', 'training', 'trained', 'published']),
  results: ModelResultsSchema.optional(),
  checkpoints: z.array(CheckpointSchema).optional(),
  features: z.array(FeatureSchema).optional(),
  created: z.string().optional(),
  lastUpdated: z.string().optional(),
});

export type ModelVersion = z.infer<typeof ModelVersionSchema>;

export const ModelSchema = z.object({
    id: z.string(),
    labId: z.string(),
    domainId: z.string(),
    outcomeId: z.string(),
    visibility: z.enum(['draft', 'private', 'public', 'unlisted', 'archived']),
    status: z.enum(['active', 'archived', 'disabled']).optional(),
    name: z.string(),
    handle: z.string().optional(),
    description: z.string(),
    picture: z.string().optional(),
    publishedVersionId: z.string().optional(),
    latestVersionId: z.string().optional(),
    subscriberCount: z.number().optional(),
    commentCount: z.number().optional(),
    created: z.string(),
    lastUpdated: z.string(),
    versions: z.array(ModelVersionSchema).optional(),
  });
  
export type Model = z.infer<typeof ModelSchema>;